import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { 
  Package, 
  Plus, 
  Calendar, 
  User, 
  TrendingUp, 
  Search, 
  MoreVertical,
  Copy,
  Trash2,
  Eye,
  Filter,
  Loader2
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/use-auth';
import { packageService, packageUtils, type PackageListItem, type PackageStatus } from '@/lib/packages';

const statusConfig = {
  draft: { label: 'Taslak', color: 'bg-warning/20 text-warning-foreground border-warning/30' },
  offered: { label: 'Teklif Verildi', color: 'bg-blue/20 text-blue-foreground border-blue/30' },
  accepted: { label: 'Kabul Edildi', color: 'bg-success/20 text-success-foreground border-success/30' },
  rejected: { label: 'Reddedildi', color: 'bg-destructive/20 text-destructive-foreground border-destructive/30' },
  expired: { label: 'Süresi Doldu', color: 'bg-muted/20 text-muted-foreground border-muted/30' },
  converted: { label: 'Dönüştürüldü', color: 'bg-primary/20 text-primary-foreground border-primary/30' },
  cancelled: { label: 'İptal Edildi', color: 'bg-secondary/20 text-secondary-foreground border-secondary/30' },
};

const Packages = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { companyId, loading: authLoading } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [packages, setPackages] = useState<PackageListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    draft: 0,
    offered: 0,
    accepted: 0,
    rejected: 0,
    expired: 0,
    converted: 0,
    cancelled: 0,
    totalValue: 0,
  });

  const filteredPackages = packages.filter(pkg => {
    const matchesSearch = pkg.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         pkg.customer_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || pkg.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const loadPackages = async () => {
    if (!companyId) return;
    
    try {
      setLoading(true);
      const [packagesData, statsData] = await Promise.all([
        packageService.getPackages(companyId),
        packageService.getPackageStats(companyId)
      ]);
      
      setPackages(packagesData);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading packages:', error);
      toast({
        title: "Hata",
        description: "Paketler yüklenirken bir hata oluştu",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId && !authLoading) {
      loadPackages();
    }
  }, [companyId, authLoading]);

  const handleDuplicate = async (pkg: PackageListItem) => {
    if (!companyId) return;
    
    try {
      await packageService.duplicatePackage(pkg.id, companyId);
      toast({
        title: "Paket Kopyalandı",
        description: `${pkg.title} kopyalandı`,
      });
      loadPackages(); // Refresh the list
    } catch (error) {
      console.error('Error duplicating package:', error);
      toast({
        title: "Hata",
        description: "Paket kopyalanırken bir hata oluştu",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (pkg: PackageListItem) => {
    try {
      await packageService.deletePackage(pkg.id);
      toast({
        title: "Paket Silindi",
        description: `${pkg.title} silindi`,
        variant: "destructive",
      });
      loadPackages(); // Refresh the list
    } catch (error) {
      console.error('Error deleting package:', error);
      toast({
        title: "Hata",
        description: "Paket silinirken bir hata oluştu",
        variant: "destructive",
      });
    }
  };

  if (authLoading || loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Package className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">Paketler</h1>
              <p className="text-sm sm:text-base text-muted-foreground">Taslakları ve tamamlanan paketleri görüntüleyin</p>
            </div>
          </div>
          
          <Button 
            className="gap-2 w-full sm:w-auto"
            onClick={() => navigate('/packages/new')}
          >
            <Plus className="w-4 h-4" />
            Yeni Paket
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          <div className="glass-panel p-4 sm:p-6">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-warning/10 flex items-center justify-center">
                <Package className="w-4 h-4 sm:w-5 sm:h-5 text-warning" />
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-bold">{stats.draft}</h3>
                <p className="text-xs sm:text-sm text-muted-foreground">Taslaklar</p>
              </div>
            </div>
          </div>

          <div className="glass-panel p-4 sm:p-6">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-bold">{stats.offered}</h3>
                <p className="text-xs sm:text-sm text-muted-foreground">Teklif Verildi</p>
              </div>
            </div>
          </div>

          <div className="glass-panel p-4 sm:p-6">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-success/10 flex items-center justify-center">
                <Package className="w-4 h-4 sm:w-5 sm:h-5 text-success" />
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-bold">{stats.converted}</h3>
                <p className="text-xs sm:text-sm text-muted-foreground">Dönüştürüldü</p>
              </div>
            </div>
          </div>

          <div className="glass-panel p-4 sm:p-6">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-accent/10 flex items-center justify-center">
                <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-accent" />
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-bold">₺{stats.totalValue.toLocaleString()}</h3>
                <p className="text-xs sm:text-sm text-muted-foreground">Toplam Değer</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              placeholder="Paket adı veya müşteri ile ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2">
                <Filter className="w-4 h-4" />
                {statusFilter === 'all' ? 'Tüm Durumlar' : statusConfig[statusFilter as keyof typeof statusConfig]?.label}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => setStatusFilter('all')}>
                Tüm Durumlar
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setStatusFilter('draft')}>
                Taslak
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setStatusFilter('offered')}>
                Teklif Verildi
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setStatusFilter('accepted')}>
                Kabul Edildi
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setStatusFilter('rejected')}>
                Reddedildi
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setStatusFilter('expired')}>
                Süresi Doldu
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setStatusFilter('converted')}>
                Dönüştürüldü
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setStatusFilter('cancelled')}>
                İptal Edildi
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Packages List */}
        <div className="glass-panel p-4 sm:p-6">
          <div className="space-y-4">
            {filteredPackages.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">Paket bulunamadı</h3>
                <p className="text-muted-foreground mb-4">
                  {searchTerm || statusFilter !== 'all' 
                    ? 'Arama veya filtrelerinizi düzenlemeyi deneyin'
                    : 'Başlamak için ilk paketinizi oluşturun'
                  }
                </p>
                {!searchTerm && statusFilter === 'all' && (
                  <Button onClick={() => navigate('/packages/new')}>
                    <Plus className="w-4 h-4 mr-2" />
                    Paket Oluştur
                  </Button>
                )}
              </div>
            ) : (
              filteredPackages.map((pkg) => (
                <div
                  key={pkg.id}
                  className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl bg-card hover:bg-accent/5 transition-all duration-200 border cursor-pointer"
                  onClick={() => navigate(`/packages/${pkg.id}`)}
                >
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Package className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                  </div>

                  <div className="flex-1 min-w-0 w-full sm:w-auto">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-2 sm:mb-1">
                      <h3 className="font-semibold truncate">{pkg.title}</h3>
                      <Badge 
                        variant="outline" 
                        className={statusConfig[pkg.status].color}
                      >
                        {statusConfig[pkg.status].label}
                      </Badge>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-xs sm:text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <User className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{pkg.customer_name}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Package className="w-3 h-3 flex-shrink-0" />
                        <span>{pkg.item_count} ürün</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3 flex-shrink-0" />
                        <span>{new Date(pkg.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 w-full sm:w-auto">
                    <div className="flex flex-col sm:text-right flex-1">
                      <div className="font-semibold text-lg">₺{pkg.total.toLocaleString()}</div>
                      <div className="text-xs text-muted-foreground">Toplam</div>
                    </div>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/packages/${pkg.id}`);
                        }}>
                          <Eye className="w-4 h-4 mr-2" />
                          Aç
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => {
                          e.stopPropagation();
                          handleDuplicate(pkg);
                        }}>
                          <Copy className="w-4 h-4 mr-2" />
                          Kopyala
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(pkg);
                          }}
                          className="text-destructive"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Sil
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Packages;